import React, { useMemo } from 'react';
import { observer } from 'mobx-react';

import UniversalDeviceDrawer from '@/comp/StationControlCenter/UniversalDeviceDrawer/UniversalDeviceDrawer';
import DetailsDrawer from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/DetailsDrawer';
import { Container, ModalWrapper } from './UniversalDeviceWidget.styles';
import { useStores } from '@/hooks/use-stores';
import {
  mapDeviceToUniversalSCC,
} from '@/comp/StationControlCenter/UniversalDataMapper/UniversalDeviceDataMapper';
import type { SCCUniversalDevice } from '@/comp/StationControlCenter/types/universal-device.types';
import { useUniversalDeviceWidgetVM } from './hooks/useUniversalDeviceWidgetVM';
import { Header } from './components/Header/Header';
import { Content } from './components/Content/Content';
import { Footer } from './components/Footer/Footer';
import { Device } from '@/store/Station/constants';
import { universalDeviceDrawerTypes } from '@/constants/deviceTypeEnum';
import type { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';

interface UniversalDeviceWidgetProps {
  device: Device;
}

const UniversalDeviceWidget = observer(({ device }: UniversalDeviceWidgetProps) => {
  const { alertStore, controlCenterStore, mapBuilderStore } = useStores();

  if (!device) return null;

  const isDetailsModalOpen = useMemo<boolean>(
    () => mapBuilderStore.getDetailsModalVisibiltyByDeviceId(device.deviceId, mapBuilderStore.isDetailsModalMinimized),
    [mapBuilderStore.selectedDevice, mapBuilderStore.isDetailsModalMinimized, device.deviceId],
  );

  const isUniversalDeviceDrawerType = universalDeviceDrawerTypes.includes(device.deviceType);

  const alerts = alertStore.filterAlertsByDeviceId(device.deviceId) || [];

  const deviceSccSchema = controlCenterStore.getDeviceSccSchemaForDevice(device.deviceId);

  const deviceSCC = mapDeviceToUniversalSCC(device, alerts, deviceSccSchema) as unknown as SCCUniversalDevice;

  const vm = useUniversalDeviceWidgetVM(deviceSCC);

  const isInMaintenance = (device as { inMaintenance?: boolean }).inMaintenance ?? vm.footer.inMaintenance;

  const handleDrawerOpen = () => {
    if (isUniversalDeviceDrawerType) {
      vm.drawer.open();
    } else {
      mapBuilderStore.setSelectedDevice(device as unknown as Entity);
      mapBuilderStore.setSelectedDeviceTitle(device.deviceType);
      mapBuilderStore.setIsDetailsModalMinimized(false);
    }
  };

  return (
    <>
      <ModalWrapper onClick={handleDrawerOpen}>
        <Container $isInMaintenance={isInMaintenance} $isPoweredOn={vm.poweredOn}>
          <Header {...vm.header} />
          <Content {...vm.content} />
          <Footer {...vm.footer} />
        </Container>
      </ModalWrapper>
      {isUniversalDeviceDrawerType ? (
        <UniversalDeviceDrawer isOpen={vm.drawer.isOpen} onClose={vm.drawer.close} deviceId={device.deviceId} />
      ) : (
        <DetailsDrawer
          isDetailsModalOpen={isDetailsModalOpen}
          entity={device as unknown as Entity}
          fromSCC={true}
        />
      )}
    </>
  );
});

export default UniversalDeviceWidget;
